import {
    useState,
    useRef,
    createRef,
} from 'react'

import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'

import {
    useLocation,
} from 'react-router-dom'
import PublicHeader from '../../publicHeader'
import BackgroundCheckStepper from '../BackgroundCheckForm/Components/BackgroundCheckStepper'
import PreCheck from './Components/PreCheck'
import Welcome from './Components/Welcome'

import preCheckFormQuery from './services/pre-check-form'
import savePreCheckConsentProgressMutation from './services/save-pre-check-consent-progress'
import submitPreCheckConsentAndStartSessionMutation from './services/submit-pre-check-consent-and-start-session'
import consentUploadMutation from './services/consent-upload'

import Overlay from '../../Components/Overlay'
import DisclosureForElectronicConsent from './Components/DisclosureForElectronicConsent'
import SummaryOfYourRightsUnderFcra from './Components/SummaryOfYourRightsUnderFcra'
import DisclosureForFcra from './Components/DisclosureForFcra'
import ExpressAuthorization from './Components/ExpressAuthorization'
import AcknowledgmentAndAuthorization from './Components/AcknowledgmentAndAuthorization'

import { getElementWithInlineStyles } from '../../helpers/component-helper'

const PreCheckForm = () => {

    const location = useLocation()

    const refToPrint = useRef()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    const {
        data,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['pre-check-form', key],
        queryFn: () => preCheckFormQuery(key),
        refetchOnWindowFocus: false,
    })

    const {
        error: savePreCheckConsentProgressError,
        isPending: savePreCheckConsentProgressIsPending,
        mutate: savePreCheckConsentProgress,
        mutateAsync: savePreCheckConsentProgressAsync,
    } = useMutation({
        mutationFn: savePreCheckConsentProgressMutation,
        onSuccess: async (d) => {

            const nextPageIndex = currentPageIndex + 1

            if (nextPageIndex === preCheckPageIndex) {
                submitPreCheckConsentAndStartSession({
                    key,
                })
                return
            }

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)
        }
    })

    const {
        error: mutationConsentUploadError,
        isPending: mutationConsentUploadIsPending,
        mutate: consentUpload,
        data: mutationConsentUploadData,
    } = useMutation({
        mutationFn: consentUploadMutation,
        onSuccess: async (d) => {
            const payload = d.payloadToSaveApplicantProgress
            const pageType = d.pageType
            const url = d.url

            const page = payload?.data?.find((d => d.type === pageType))

            if (page?.data) {
                page.data.htmlUrl = url
            }

            savePreCheckConsentProgress(payload)
        },
    })

    const {
        error: submitPreCheckConsentAndStartSessionError,
        isPending: submitPreCheckConsentAndStartSessionIsPending,
        mutate: submitPreCheckConsentAndStartSession,
        mutateAsync: submitPreCheckConsentAndStartSessionAsync,
    } = useMutation({
        mutationFn: submitPreCheckConsentAndStartSessionMutation,
        onSuccess: async (d) => {

            const nextPageIndex = currentPageIndex + 1

            await refetch()

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)

        }
    })

    const [currentPageIndex, setCurrentPageIndex] = useState(0)
    const [currentStep, setCurrentStep] = useState(0)

    const pageRefs = useRef([])

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen my-4">
                <span className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
            </div>
        )
    }
    if (error) {
        return (
            <div>Error: {error.message}</div>
        )
    }

    if (!data) {
        return (
            <div>
                Data is not loaded yet.
            </div>
        )
    }

    const currentPage = data.Pages[currentPageIndex]

    let currentRef = pageRefs.current[currentPageIndex]

    if (!currentRef) {
        currentRef = createRef()
        pageRefs.current[currentPageIndex] = currentRef
    }

    const steps = data.Pages.map(p => p.title)

    const handlePrev = () => {
        const prevPageIndex = currentPageIndex - 1
        if (prevPageIndex < 0) {
            return
        }
        setCurrentPageIndex(prevPageIndex)

        setCurrentStep(prevPageIndex)

    }

    let nextButtonText = currentPageIndex === data.Pages.length - 1 ? "Submit" : "Next"

    const preCheckPageIndex = data.Pages.findIndex(page => page.type === "pre_check")

    const handleNext = () => {
        const nextPageIndex = currentPageIndex + 1
        const currentPageData = currentRef.current.validateAndGetData()
        if (!currentPageData.isValid) {
            return
        }

        currentPage.data = currentPageData.value

        const dataToSubmit = data.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
            }
        })

        const payload = {
            key,
            data: dataToSubmit,
        }


        if (refToPrint.current) {

            syncCheckedAttributes(refToPrint.current)

            consentUpload({
                dataForUploadUrl: {
                    key,
                    consentType: currentPage.type,
                },
                file: new File([getElementWithInlineStyles(refToPrint.current),], `${currentPage.type}.html`, { type: "text/html" }),
                payloadToSaveApplicantProgress: payload,
                pageType: currentPage.type,
            })

            return
        }

        savePreCheckConsentProgress(payload)

    }


    return (
        <div className="h-screen flex flex-col bg-gray-50">

            <PublicHeader referenceNumber={data.referenceNumber} />

            {
                currentPage.type !== "welcome" && (
                    <BackgroundCheckStepper steps={steps} currentStep={currentStep} />
                )
            }

            <div className="flex flex-col flex-grow items-center justify-center">
                {
                    currentPage.type === "welcome" &&
                    <Welcome
                        ref={currentRef}
                        data={currentPage.data}
                        company={currentPage.company}
                        handleNext={handleNext}
                    />
                }
                {
                    currentPage.type === "disclosure_for_electronic_consent" &&
                    <DisclosureForElectronicConsent
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                    />
                }
                {
                    currentPage.type === "summary_of_rights_under_fcra" &&
                    <SummaryOfYourRightsUnderFcra
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "disclosure_for_fcra" &&
                    <DisclosureForFcra
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "express_authorization" &&
                    <ExpressAuthorization
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "acknowledgment_and_authorization" &&
                    <AcknowledgmentAndAuthorization
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "pre_check" &&
                    <PreCheck
                        ref={currentRef}
                        data={currentPage.data}
                        company={currentPage.company}
                    />
                }

            </div>



            {(false) && (
                <p className="mt-1 text-base text-red-600 py-4 mx-auto">
                    Error
                </p>
            )}

            {
                !(
                    currentPage.type === "welcome" ||
                    currentPage.type === "pre_check"
                ) && (
                    <div className="flex justify-end gap-6 p-4 border-t">
                        <button
                            onClick={handlePrev}
                            disabled={currentPageIndex === 0}
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            Prev
                        </button>
                        <button
                            onClick={handleNext}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            {nextButtonText}
                        </button>
                    </div>
                )
            }

            {/* Overlay */}
            {
                (
                    isFetching ||
                    savePreCheckConsentProgressIsPending ||
                    submitPreCheckConsentAndStartSessionIsPending ||
                    mutationConsentUploadIsPending
                ) &&
                <Overlay />
            }

        </div>

    )
}

export default PreCheckForm

function syncCheckedAttributes(container) {
    const inputs = container.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => {
        if (input.checked) {
            input.setAttribute("checked", "checked");
        } else {
            input.removeAttribute("checked");
        }
    });
}
